/*
 * ═══════════════════════════════════════════════════════════════════════════
 * ARCHITECTURE RULE: Transport commands (pause, play, stop, playUri)
 * may ONLY be called from:
 *
 *   1. QueueController — track advance, _playTrack
 *   2. Direct user gesture handlers — button onClick, lock screen controls
 *   3. authorizeIosPause() gate — iOS media session only
 *
 * NEVER call transport commands from:
 *   - Zustand subscribers
 *   - useEffect hooks watching zones or renderers
 *   - BATON event handlers
 *   - Discovery poll callbacks
 *
 * This rule exists because a Zustand subscriber calling pause() caused
 * a full RAAT cascade failure at Axpona 2026.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useMaestroStore } from '../store/maestroStore';
import { isLimitedRenderer } from './types';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { detectPlatform } from '../utils/platform';
import { guardAbsentNetworkRenderer, type HydrateCommand } from './rendererConnectivity';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { durationFromTrackRow } from '../utils/trackDuration';
import { noteSeekDispatched } from './positionReconcile';
import { markUserPlaybackStarted } from './playbackSession';
import { isDsd, getDsdSampleRateHz } from '../utils/formatDetection';
import { shouldRouteLocalExclusive } from '../utils/exclusiveDeviceGate';
import {
  resolveExclusiveDeviceName,
  logLocalOutputExclusive,
  logLocalOutputShared,
} from '../utils/localOutputLogging';
import {
  clearExclusiveErrorOnPlaybackConfirmed,
  exclusivePlayWithDeviceBusyRetry,
  isRecoverableExclusiveFailure,
  releaseSharedFallbackBeforeExclusive,
  shouldShowFatalExclusiveError,
} from '../utils/exclusiveFallbackUI';
import { normalizeExclusiveDsdOutputMode } from '../utils/dsdOutputBadge';
import { shouldBlockWindowsExclusivePlay } from '../utils/exclusiveKillSwitch';
import {
  normalizeExclusiveStreamError,
  shouldExclusivePollerAdvanceTrackEnd,
} from './exclusivePollerSignals';
import { verifyRendererPlayingAfterTimeout } from '../lib/ipcTransportDegraded';

/** @deprecated No-op — iOS pause gate removed. User-initiated pauses are gated by userInitiated flag. */
export function authorizeIosPause(): void {
  // Kept as a no-op for callers that haven't been cleaned up yet.
}

/** Minimum gap between consecutive RAAT/native transport dispatches (ms).
 *  PMG and other Roon Ready devices drop commands that arrive <500ms apart. */
const RAAT_MIN_GAP_MS = 500;
let _lastRaatCommandAt = 0;

async function _enforceRaatGap(): Promise<void> {
  const elapsed = Date.now() - _lastRaatCommandAt;
  if (elapsed < RAAT_MIN_GAP_MS) {
    await new Promise(r => setTimeout(r, RAAT_MIN_GAP_MS - elapsed));
  }
  _lastRaatCommandAt = Date.now();
}

type ConnectorModule = typeof import('../lib/connector');

type PendingCommand = {
  type: 'play' | 'pause' | 'playUri';
  execute: () => Promise<void>;
  resolve: () => void;
  reject: (err: any) => void;
};

class TransportCommandImpl {
  private _busy = false;
  private _queue: PendingCommand[] = [];
  private _audioRef: HTMLAudioElement | null = null;
  /** WebAudioRenderer registers full teardown (HLS, transitions, src clear). */
  private _resetLocalAudioFn: (() => void) | null = null;
  /** WebAudioRenderer tracks currentSrcRef when playUri bypasses trackChanged. */
  private _commitLocalSourceFn: ((url: string) => void) | null = null;
  private _commandTimeout = 3000;

  private _volumeEcho: { target: number; timestamp: number } | null = null;
  private _volumeEchoWindow = 3000;
  private _seekGrace: { target: number; timestamp: number } | null = null;
  private _seekGraceWindow = 4000;
  private _volumeDragUntil = 0;

  private _playUriFails = 0;
  private _playUriLastFail = 0;
  private _lastPlayUriTimestamp = 0;
  /** Set when playUri native/connector dispatch succeeds — used to ignore stale sync pauses. */
  private _lastPlayUriSuccessAt = 0;
  private _volumeDebounceTimer: number | null = null;

  private _connector: ConnectorModule | null = null;
  private _iosCheckLogged = false;

  /** iOS playUri confirmation generation — stale callbacks ignore old waits */
  private _iosPlayConfirmGen = 0;
  /** Fix D: delayed ghost self-heal */
  private _ghostPlaybackTimer: ReturnType<typeof setTimeout> | null = null;

  /** Exclusive audio engine state poller interval */
  private _exclusivePoller: ReturnType<typeof setInterval> | null = null;
  /** Track whether we're in exclusive native playback mode */
  private _exclusiveActive = false;
  /** Prevents duplicate advanceTrackEnd while the poller interval is still winding down */
  private _exclusiveEndAdvancePending = false;

  private async _getConnector(): Promise<ConnectorModule> {
    if (!this._connector) {
      this._connector = await import('../lib/connector');
    }
    return this._connector;
  }

  /** Check if we should route audio through the native exclusive engine (bit-perfect USB DAC path). */
  async shouldUseExclusive(): Promise<boolean> {
    return this._shouldUseExclusive();
  }

  /** Check if we should route audio through the native exclusive engine (bit-perfect USB DAC path). */
  private async _shouldUseExclusive(): Promise<boolean> {
    if (!this._isTauri()) return false;

    // KILL SWITCH — remove when v164 ships
    if (shouldBlockWindowsExclusivePlay()) return false;

    const store = useMaestroStore.getState();
    return shouldRouteLocalExclusive(
      store.discovery?.activeZone as any,
      store.selectedUsbDac,
    );
  }

  /** Clear the exclusive poller interval without dropping exclusive mode (handoff / pause). */
  private _clearExclusivePollerInterval() {
    if (this._exclusivePoller) {
      clearInterval(this._exclusivePoller);
      this._exclusivePoller = null;
    }
  }

  /** Start polling the exclusive engine for position/state updates */
  private _startExclusivePoller() {
    this._clearExclusivePollerInterval();
    this._exclusiveActive = true;
    this._exclusiveEndAdvancePending = false;
    this._exclusivePoller = setInterval(async () => {
      try {
        const C = await this._getConnector();
        const state = await C.exclusiveGetState();
        const store = useMaestroStore.getState();

        const dsdMode = normalizeExclusiveDsdOutputMode(state.dsd_output_mode);
        const sourceDsdHz = typeof state.source_dsd_rate_hz === 'number'
          ? state.source_dsd_rate_hz
          : 0;
        if (dsdMode && sourceDsdHz > 0) {
          store.setExclusiveDsdOutputTruth(dsdMode, sourceDsdHz);
        } else if (state.dsd_output_mode != null) {
          store.setExclusiveDsdOutputTruth(null, null);
        }

        const streamError = normalizeExclusiveStreamError(state.stream_error);
        if (streamError) {
          this._clearExclusivePollerInterval();
          store.setPlaybackState('paused');
          store.setRendererHasStream(false);
          store.setTransportPlaybackError(streamError);
          diagnosticLog('error', 'transport', 'exclusive_stream_error', { error: streamError });
          return;
        }

        if (store.playbackState === 'playing') {
          const duration = store.duration || state.duration_secs || 0;
          // Local loopback URLs may report is_playing:false / position_secs:0 while audio
          // plays — fall back to store.position (wall-clock ticker) in that case.
          const position = (state.is_playing || state.position_secs > 0)
            ? state.position_secs
            : store.position;
          store.updateTime(position, duration);
        }

        const effectiveDuration = store.duration || state.duration_secs || 0;
        const effectivePosition = (state.is_playing || state.position_secs > 0)
          ? state.position_secs
          : store.position;
        // v11.0.172+: connector exposes ended at drain/EOS (primary signal).
        // Pre-11.0.172 fallback: advance at/after reported duration when ended is absent.
        if (
          shouldExclusivePollerAdvanceTrackEnd({
            state,
            playbackState: store.playbackState,
            effectiveDuration,
            effectivePosition,
            endAdvancePending: this._exclusiveEndAdvancePending,
          })
        ) {
          this._exclusiveEndAdvancePending = true;
          this._clearExclusivePollerInterval();
          console.log('[TCmd] Exclusive track ended — advancing via advanceTrackEnd');
          const { QueueController } = await import('./QueueController');
          try {
            await QueueController.advanceTrackEnd();
          } finally {
            this._exclusiveEndAdvancePending = false;
          }
        }
      } catch (err) {
        console.warn('[TCmd] Exclusive poller error:', err);
      }
    }, 500);
  }

  private _stopExclusivePoller() {
    this._clearExclusivePollerInterval();
    this._exclusiveActive = false;
    this._exclusiveEndAdvancePending = false;
    useMaestroStore.getState().setExclusiveDsdOutputTruth(null, null);
  }

  registerAudioElement(el: HTMLAudioElement) {
    this._audioRef = el;
  }

  unregisterAudioElement() {
    this._audioRef = null;
  }

  registerLocalAudioReset(fn: () => void) {
    this._resetLocalAudioFn = fn;
  }

  unregisterLocalAudioReset() {
    this._resetLocalAudioFn = null;
  }

  registerLocalSourceCommit(fn: (url: string) => void) {
    this._commitLocalSourceFn = fn;
  }

  unregisterLocalSourceCommit() {
    this._commitLocalSourceFn = null;
  }

  /**
   * Stop and reset the browser System Audio element — pause, destroy HLS,
   * clear src, cancel in-flight transitions. WebAudioRenderer registers
   * the full implementation; falls back to pause+clear when unmounted.
   */
  resetLocalAudioElement(): void {
    if (this._resetLocalAudioFn) {
      try {
        this._resetLocalAudioFn();
        return;
      } catch (e) {
        console.warn('[TransportCommand] resetLocalAudioElement callback failed:', e);
      }
    }
    const el = this._audioRef;
    if (!el) return;
    try {
      el.pause();
      el.removeAttribute('src');
      el.load();
    } catch (e) {
      console.warn('[TransportCommand] resetLocalAudioElement fallback failed:', e);
    }
  }

  /**
   * Defensive helper: halt the browser's local HTML5 audio element when
   * switching playback surfaces (e.g., radio → network renderer).
   */
  pauseLocalAudioElement(): void {
    this.resetLocalAudioElement();
  }

  private _isMultizoneActive(): boolean {
    const mz = useMaestroStore.getState().multizone;
    return mz.enabled && mz.groupedZones.length >= 2;
  }

  private async _multizonePlayAll(): Promise<void> {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;
    if (!this._isTauri()) return;
    const C = await this._getConnector();

    const streamUrl = store.currentStreamUrl;
    const formatId = store.currentStreamFormatId || 6;
    const track = store.currentTrack;

    // Route resume through the Connector's sync engine so it re-sends
    // SetAVTransportURI (needed for WiiM which loses its URI on Stop-for-Pause),
    // applies stagger timing, and spawns a fresh drift monitor.
    if (streamUrl && track) {
      console.log('[TransportCommand] multizonePlayAll — routing through multizone_play for staggered resume');
      try {
        await C.multizonePlay(
          zones.map(z => ({ renderer_id: z.zoneId, volume: z.actualVolume ?? null })),
          streamUrl,
          track?.title || '', track?.artist || '', track?.album || '',
          formatId,
          Math.round(track?.duration || 0),
          track?.artworkUrl || track?.artUrl,
        );
        store.setRendererHasStream(true);
        return;
      } catch (err: any) {
        console.warn('[TransportCommand] multizone_play IPC failed, falling back to per-device play:', err?.message || err);
      }
    }

    // Fallback: per-device play (no stagger, no drift monitor)
    const results = await Promise.allSettled(
      zones.map(z => C.play(z.zoneId).catch(err => {
        console.error(`[TransportCommand] multizone play failed for ${z.zoneName}:`, err);
        throw err;
      }))
    );
    console.log('[TransportCommand] multizonePlayAll fallback results:', results.map(r => r.status));
  }

  private async _multizonePauseAll(): Promise<void> {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;
    if (!this._isTauri()) return;
    const C = await this._getConnector();

    // Cancel the drift monitor FIRST — prevents wild seek corrections on paused devices
    try {
      await C.multizonePause();
    } catch (err: any) {
      console.warn('[TransportCommand] multizonePause IPC failed (drift monitor may persist):', err?.message || err);
    }

    // Then send per-device pause commands
    await Promise.allSettled(
      zones.map(z => C.pause(z.zoneId).catch(err => {
        console.error(`[TransportCommand] multizone pause failed for ${z.zoneName}:`, err);
      }))
    );
  }

  private async _multizoneStopAll(): Promise<void> {
    if (!this._isTauri()) return;
    const C = await this._getConnector();

    // Connector's multizone_stop cancels the drift monitor AND stops all renderers
    try {
      await C.multizoneStop();
    } catch (err: any) {
      console.warn('[TransportCommand] multizoneStop IPC failed, falling back to per-device stop:', err?.message || err);
      const zones = useMaestroStore.getState().multizone.groupedZones;
      await Promise.allSettled(
        zones.map(z => C.stop(z.zoneId).catch(stopErr => {
          console.error(`[TransportCommand] multizone stop failed for ${z.zoneName}:`, stopErr);
        }))
      );
    }
  }

  private async _multizoneSeekAll(positionSeconds: number): Promise<void> {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;
    if (!this._isTauri()) return;
    const C = await this._getConnector();
    const secs = Math.floor(positionSeconds);
    await Promise.allSettled(
      zones.map(z => C.seek(z.zoneId, secs).catch(err => {
        console.error(`[TransportCommand] multizone seek failed for ${z.zoneName}:`, err);
      }))
    );
  }

  /** Re-hydrate activeConnectorRenderer from activeZone when needed.
   *  Preserves pinned network selection during reconnecting/unreachable states. */
  private _hydrateConnectorRendererFromActiveZone(command: HydrateCommand = 'volume'): boolean {
    if (!guardAbsentNetworkRenderer(command)) {
      return false;
    }

    const s = useMaestroStore.getState();
    const zones = s.discovery?.discoveredZones || [];
    const hasBridge = this._isTauri() || this._isIOS();

    const az = s.discovery?.activeZone as { id?: string; name?: string; type?: string } | undefined;
    if (!s.activeConnectorRenderer && az?.id && hasBridge) {
      if (az.type === 'local' || az.type === 'browser'
          || az.id === 'this_computer' || az.id === 'local_computer' || az.id === 'this_device') {
        return true;
      }
      if (zones.length > 0 && zones.some((z: any) => z.id === az.id)) {
        s.setActiveConnectorRenderer({ id: az.id, name: az.name || az.id });
      }
    }
    return true;
  }

  /**
   * RESUME ONLY — never initiates new playback, never calls QueueController.
   *
   * play() resumes an existing stream. If there is no currentStreamUrl,
   * play() is a no-op. New playback is ONLY initiated by QueueController
   * (playAll, playFrom, playQueueTrack, next, previous) which calls
   * TransportCommand.playUri() with an already-resolved URI.
   */
  async play() {
    if (!this._hydrateConnectorRendererFromActiveZone('play')) return;
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    const url = store.currentStreamUrl;

    if (!url) {
      console.log('[Transport] play() — no active URI, no-op. Select a track to start playback.');
      return;
    }

    // Multizone resume
    if (this._isMultizoneActive() && store.rendererHasStream) {
      store.setPlaybackState('playing');
      return this._enqueue('play', async () => {
        try {
          await this._multizonePlayAll();
        } catch (err: any) {
          console.error('[TransportCommand.play] multizone resume failed:', err?.message || err);
          useMaestroStore.getState().setPlaybackState('paused');
        }
      });
    }

    // Renderer present but stream not yet sent — re-send the cached URL
    if (renderer && !store.rendererHasStream) {
      const formatId = store.currentStreamFormatId || 27;
      console.log('[Transport] play() — renderer has no stream, re-sending cached URI to', renderer.name);
      return this.playUri(url, renderer.id, formatId, store.currentTrack);
    }

    // Exclusive engine resume
    if (this._exclusiveActive) {
      store.setPlaybackState('playing');
      return this._enqueue('play', async () => {
        try {
          const C = await this._getConnector();
          await C.exclusiveResume();
          this._startExclusivePoller();
        } catch (err: any) {
          console.error('[TCmd] exclusive resume failed:', err?.message || err);
          useMaestroStore.getState().setPlaybackState('paused');
        }
      });
    }

    // iOS native without a network renderer
    if (!renderer && !this._audioRef && this._isIOS()) {
      store.setPlaybackState('playing');
      this._iosPostMessage('play', {});
      return;
    }

    if (!renderer && !this._audioRef) {
      console.log('[Transport] play() — no renderer, no audioRef, no-op');
      return;
    }

    // Standard resume: send play to renderer and/or local audio element
    store.setPlaybackState('playing');

    return this._enqueue('play', async () => {
      const s = useMaestroStore.getState();
      const r = s.activeConnectorRenderer;
      const hasBridge = this._isTauri() || this._isIOS();

      try {
        if (r && this._isTauri()) {
          const C = await this._getConnector();
          await C.play(r.id);
        } else if (r && this._isIOS()) {
          this._iosSendCommand(r.id, 'play');
        }

        if (this._audioRef && this._audioRef.paused && this._audioRef.currentSrc) {
          if (!r || !hasBridge) {
            await this._audioRef.play();
          }
        }
      } catch (err: any) {
        console.error('[Transport] play() resume failed:', err?.message || err);
        useMaestroStore.getState().setPlaybackState('paused');
      }
    });
  }

  isExclusiveActive(): boolean {
    return this._exclusiveActive;
  }

  async stopExclusiveEngine(): Promise<void> {
    if (!this._exclusiveActive) return;
    this._stopExclusivePoller();
    if (this._isTauri()) {
      try {
        const C = await this._getConnector();
        await C.exclusiveStop();
      } catch (err: any) {
        console.warn('[TCmd] exclusiveStop failed:', err?.message || err);
      }
    }
  }

  /** @param userInitiated - must be true for tap / lock-screen / media-session pause so the Axpona guard does not block real user stops */
  async pause(userInitiated = false) {
    if (userInitiated) {
      void import('./remoteAdvanceControl').then(({ markUserTransportStop }) => {
        markUserTransportStop();
      });
      void import('../radio/ellaRadioSessionState').then(({ disarmRadioAutoResume }) => {
        disarmRadioAutoResume('user_pause');
      });
    }
    // Observer devices must NEVER send transport commands to the renderer —
    // the controller owns the renderer. Observers only update the local store.
    if (!userInitiated) {
      try {
        if (getSyncHandler().isObserving()) {
          console.debug('[TCmd] pause blocked — observing (non-user)');
          return;
        }
      } catch {}
    }
    // Block spurious non-user pauses while playback is live on any surface.
    if (!userInitiated) {
      const { rendererHasStream, playbackState } = useMaestroStore.getState();
      const playbackLive = playbackState === 'playing' || playbackState === 'loading';
      if ((rendererHasStream || this._exclusiveActive) && playbackLive) {
        console.debug('[TCmd] pause blocked — playback live (non-user command rejected)');
        return;
      }
    }
    // Suppress spurious auto-pauses that fire right after playUri succeeds.
    // User-initiated pauses (button taps, lock screen, media session) MUST
    // always go through — never block an explicit user action.
    if (!userInitiated && this._lastPlayUriSuccessAt > 0 && Date.now() - this._lastPlayUriSuccessAt < 500) {
      console.log('[TCmd] pause BLOCKED — too soon after playUri (non-user-initiated)');
      return;
    }

    if (!this._hydrateConnectorRendererFromActiveZone('pause')) return;
    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    console.log('[TransportCommand.pause] renderer:', renderer?.name, renderer?.id,
      'isTauri:', this._isTauri(), 'isIOS:', this._isIOS(),
      'multizone:', this._isMultizoneActive());

    // Multizone pause: send pause to all zones in parallel
    if (this._isMultizoneActive()) {
      useMaestroStore.getState().setPlaybackState('paused');
      return this._enqueue('pause', async () => {
        try {
          await this._multizonePauseAll();
        } catch (err: any) {
          console.error('[TransportCommand.pause] multizone pause failed:', err?.message || err);
        }
      });
    }

    // Exclusive engine pause
    if (this._exclusiveActive) {
      console.log('[TCmd] pause → exclusive engine pause');
      useMaestroStore.getState().setPlaybackState('paused');
      this._clearExclusivePollerInterval();
      return this._enqueue('pause', async () => {
        try {
          const C = await this._getConnector();
          await C.exclusivePause();
        } catch (err: any) {
          console.error('[TCmd] exclusive pause failed:', err?.message || err);
        }
      });
    }

    // iOS native without a network renderer: send pause through the native bridge
    // for local AVPlayer playback. WebAudioRenderer is not mounted on iOS.
    if (!renderer && !this._audioRef && this._isIOS()) {
      console.log('[TCmd] pause → iOS local playback (native bridge)');
      useMaestroStore.getState().setPlaybackState('paused');
      this._iosPostMessage('pause', {});
      return;
    }

    if (!renderer && !this._audioRef) {
      console.error('[TransportCommand.pause] NO RENDERER and no audioRef — nowhere to send command');
      useMaestroStore.getState().setPlaybackState('paused');
      return;
    }

    useMaestroStore.getState().setPlaybackState('paused');

    return this._enqueue('pause', async () => {
      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      const hasBridge = this._isTauri() || this._isIOS();
      const az = store.discovery?.activeZone as any;

      try {
        if (renderer && this._isTauri()) {
          const C = await this._getConnector();
          await C.pause(renderer.id);
        } else if (renderer && this._isIOS()) {
          this._iosSendCommand(renderer.id, 'pause', true);
        }

        if (this._audioRef && !this._audioRef.paused) {
          this._audioRef.pause();
        }
      } catch (err: any) {
        console.error('[TransportCommand.pause] failed (keeping paused — device may be unreachable):', err?.message || err);
        if (this._audioRef && !this._audioRef.paused) {
          try { this._audioRef.pause(); } catch {}
        }
      }
    });
  }

  async seek(positionSeconds: number) {
    const store = useMaestroStore.getState();
    const r = store.activeConnectorRenderer;
    const az = store.discovery?.activeZone as any;
    const hadDuration = (store.duration || durationFromTrackRow(store.currentTrack)) > 0;
    const source = store.currentTrack?.source || 'unknown';
    const rendererLabel = r?.name || az?.name || 'local';

    if (!this._hydrateConnectorRendererFromActiveZone('seek')) {
      diagnosticLog('warn', 'transport', 'seek_failed', {
        renderer: rendererLabel,
        source,
        had_duration: hadDuration,
        reason: 'renderer_unavailable',
      });
      return;
    }

    diagnosticLog(
      'info',
      'transport',
      `Command dispatched: seek to ${Math.floor(positionSeconds)}s`,
      {
        command: 'seek',
        target: rendererLabel,
        rendererType: az?.transport_type || az?.type,
        had_duration: hadDuration,
        source,
      },
    );

    const secs = Math.floor(positionSeconds);
    this._seekGrace = { target: secs, timestamp: Date.now() };
    noteSeekDispatched(secs);
    store.updateTime(positionSeconds, store.duration);

    // Multizone seek: send to all zones in parallel
    if (this._isMultizoneActive()) {
      try {
        await this._multizoneSeekAll(positionSeconds);
      } catch (err: any) {
        diagnosticLog('warn', 'transport', 'seek_failed', {
          renderer: rendererLabel,
          source,
          had_duration: hadDuration,
          reason: String(err?.message || err || 'multizone_seek_failed'),
        });
      }
      return;
    }

    const renderer = store.activeConnectorRenderer;

    try {
      if (renderer && this._isTauri()) {
        const C = await this._getConnector();
        await C.seek(renderer.id, secs);
      } else if (renderer && this._isIOS()) {
        this._iosPostMessage('seek', { deviceId: renderer.id, seconds: secs });
      } else if (this._audioRef) {
        this._audioRef.currentTime = positionSeconds;
      } else {
        console.error('[TransportCommand.seek] NO RENDERER and no audioRef — nowhere to send command');
        diagnosticLog('warn', 'transport', 'seek_failed', {
          renderer: rendererLabel,
          source,
          had_duration: hadDuration,
          reason: 'no_renderer_or_audio_ref',
        });
      }
    } catch (err: any) {
      diagnosticLog('warn', 'transport', 'seek_failed', {
        renderer: rendererLabel,
        source,
        had_duration: hadDuration,
        reason: String(err?.message || err || 'seek_dispatch_failed'),
      });
      throw err;
    }
  }

  async volume(level: number) {
    if (!this._hydrateConnectorRendererFromActiveZone('volume')) return;
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;

    if (!renderer && !this._audioRef) {
      return;
    }

    this._volumeEcho = { target: level, timestamp: Date.now() };
    store.setVolume(level);
    const az = store.discovery?.activeZone as any;
    diagnosticLog(
      'info',
      'volume',
      `Volume command: ${Math.round(level)} to ${renderer?.name || az?.name || 'local'}`,
      {
        level: Math.round(level),
        renderer: renderer?.name,
        rendererType: az?.transport_type || az?.type,
        muted: !!store.muted,
      },
    );

    if (renderer && this._isTauri()) {
      const C = await this._getConnector();
      await C.setVolume(renderer.id, Math.round(level));
    } else if (renderer && this._isIOS()) {
      this._iosPostMessage('setVolume', {
        deviceId: renderer.id,
        level: Math.round(level),
      });
    } else if (this._audioRef) {
      this._audioRef.volume = level / 100;
    }
  }

  isPlayUriRecent(withinMs = 8000): boolean {
    return this._lastPlayUriTimestamp > 0 && (Date.now() - this._lastPlayUriTimestamp) < withinMs;
  }

  /** True for 2s after playUri success — suppress stale BATON/sync/renderer paused state. */
  isInPlayUriPostSuccessSilenceWindow(): boolean {
    return this._lastPlayUriSuccessAt > 0 && Date.now() - this._lastPlayUriSuccessAt < 2000;
  }

  startVolumeDrag(): void {
    this._volumeDragUntil = Infinity;
  }

  endVolumeDrag(): void {
    this._volumeDragUntil = Date.now() + 3000;
  }

  isVolumeEchoSuppressed(reportedLevel: number): boolean {
    if (Date.now() < this._volumeDragUntil) return true;
    if (!this._volumeEcho) return false;
    const elapsed = Date.now() - this._volumeEcho.timestamp;
    if (elapsed > this._volumeEchoWindow) {
      this._volumeEcho = null;
      return false;
    }
    return reportedLevel !== this._volumeEcho.target;
  }

  /** True while renderer position polls should not snap back before seek lands. */
  shouldSuppressStaleRendererPositionAfterSeek(reportedSecs: number): boolean {
    if (!this._seekGrace) return false;
    const elapsed = Date.now() - this._seekGrace.timestamp;
    if (elapsed > this._seekGraceWindow) {
      this._seekGrace = null;
      return false;
    }
    const target = this._seekGrace.target;
    return reportedSecs < target - 2 && Math.abs(reportedSecs - target) > 3;
  }

  private _logPlayUriUrl(url: string): string {
    if (!url) return '(empty)';
    return url.length > 140 ? `${url.slice(0, 140)}…` : url;
  }

  /**
   * iOS: playUri is fire-and-forget — wait for native transport PLAYING or store position > 0.
   */
  private _waitForIOSPlaybackEvidence(rendererId: string, timeoutMs: number, generation: number): Promise<boolean> {
    return new Promise((resolve) => {
      let settled = false;
      const onTransport = (ev: Event) => {
        const d = (ev as CustomEvent).detail || {};
        if (generation !== this._iosPlayConfirmGen) { finish(false); return; }
        if (d.deviceId !== rendererId) return;
        if (d.state === 'PLAYING') finish(true);
      };
      const unsub = useMaestroStore.subscribe((state) => {
        if (generation !== this._iosPlayConfirmGen) { finish(false); return; }
        if (state.activeConnectorRenderer?.id !== rendererId) return;
        if ((state.position || 0) > 0) finish(true);
      });
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        try { window.removeEventListener('maestroTransportState', onTransport as EventListener); } catch { /* ignore */ }
        try { unsub(); } catch { /* ignore */ }
        try { clearTimeout(timer); } catch { /* ignore */ }
        resolve(ok);
      };
      const timer = setTimeout(() => finish(false), timeoutMs);
      window.addEventListener('maestroTransportState', onTransport as EventListener);
    });
  }

  private _clearGhostPlaybackTimer(): void {
    if (this._ghostPlaybackTimer) {
      clearTimeout(this._ghostPlaybackTimer);
      this._ghostPlaybackTimer = null;
    }
  }

  private _iosDispatchPlayUriPayload(
    uri: string,
    rendererId: string,
    formatId: number,
    track: any,
  ): void {
    this._iosPostMessage('playUri', {
      rendererId,
      uri,
      formatId,
      title: track?.title || '',
      artist: track?.artist || '',
      album: track?.album || '',
      durationSecs: Math.round(track?.duration || 0),
      artUrl: track?.artworkUrl || track?.artUrl || '',
    });
  }

  /**
   * Runs AFTER the playUri mutex releases — never block pause/volume/seek.
   * Observes PLAYING/position evidence; does not re-dispatch when playUri succeeded.
   */
  private _iosBackgroundConfirmPlayback(
    uri: string,
    rendererId: string,
    formatId: number,
    track: any,
    generation: number,
    options?: { skipGhostWatchdog?: boolean },
    playUriSucceeded = false,
  ): void {
    void (async () => {
      let ok = await this._waitForIOSPlaybackEvidence(rendererId, 3000, generation);
      if (!ok) {
        // Bail if a newer playUri has already been dispatched (track changed)
        if (generation !== this._iosPlayConfirmGen) return;
        // Also check the store's current track hasn't changed
        const curId = useMaestroStore.getState().currentTrack?.id;
        const origId = track?.id;
        if (origId != null && curId != null && String(curId) !== String(origId)) return;

        // Belt-and-suspenders: transport_play_uri already returned success — wall-clock
        // position ticker supplies position>0; never double-dispatch playUri on a timer.
        if (playUriSucceeded) {
          console.warn(
            '[TCmd] playUri watchdog: no native playback event yet — skipping retry (playUri succeeded)',
          );
          return;
        }

        console.warn('[TCmd] playUri watchdog: no playback confirmed after 3s — retrying (background)');
        this._iosDispatchPlayUriPayload(uri, rendererId, formatId, track);
        ok = await this._waitForIOSPlaybackEvidence(rendererId, 3000, generation);
      }
      if (!ok) {
        console.warn('[TCmd] iOS playback not confirmed after retry — leaving state (native events may be absent)');
      }
    })();
  }

  /** Fix D: 5s self-heal if UI says playing but no progress on iOS network renderer */
  private _scheduleGhostPlaybackHeal(uri: string, rendererId: string, formatId: number, track: any): void {
    this._clearGhostPlaybackTimer();
    if (!this._isIOS()) return;

    const scheduledTrackId = track?.id != null ? String(track.id) : null;

    this._ghostPlaybackTimer = setTimeout(async () => {
      this._ghostPlaybackTimer = null;
      const { msSinceLastPlayTrackCompleted } = await import('./QueueController');
      const s = useMaestroStore.getState();

      if (s.playbackState !== 'playing') return;
      if (!s.rendererHasStream) return;
      if (msSinceLastPlayTrackCompleted() < 5000) return;
      if (this._lastPlayUriSuccessAt > 0 && Date.now() - this._lastPlayUriSuccessAt < 2000) return;

      const nowId = s.currentTrack?.id != null ? String(s.currentTrack.id) : null;
      if (scheduledTrackId !== nowId) return;

      if (!s.activeConnectorRenderer || s.activeConnectorRenderer.id !== rendererId) return;
      if ((s.position || 0) > 0) return;

      console.warn(
        '[Watchdog] iOS ghost playback detected — position still 0 after playUri (log only, no re-dispatch)',
      );
    }, 5000);
  }

  async playUri(
    uri: string,
    rendererId: string,
    formatId: number,
    track: any,
    options?: { skipGhostWatchdog?: boolean; suppressExclusiveErrorUi?: boolean },
  ) {
    markUserPlaybackStarted();
    console.log(`[Transport] playUri: track=${track?.id || '?'} renderer=${rendererId || 'local'}`);

    if (!rendererId && !this._audioRef) {
      console.log('[TCmd] playUri BLOCKED: reason=no rendererId and no audioRef');
      return;
    }

    if (this._playUriFails >= 2 && Date.now() - this._playUriLastFail < 10000) {
      console.log('[TCmd] playUri BLOCKED: reason=cooldown after repeated failures');
      console.warn('[TransportCommand.playUri] Cooldown active — renderer failed', this._playUriFails, 'times recently. Waiting 10s before retry.');
      useMaestroStore.getState().setPlaybackState('paused');
      return;
    }
    if (Date.now() - this._playUriLastFail > 15000) {
      this._playUriFails = 0;
    }

    this._clearGhostPlaybackTimer();

    const store = useMaestroStore.getState();
    store.setCurrentTrack(track);
    store.setCurrentStreamUrl(uri);
    store.setCurrentStreamFormatId(formatId);

    // For renderer paths, stay at 'loading' until the renderer confirms PLAYING
    // via TransportState events. This prevents the synthetic position ticker
    // (useRemotePositionTracker) from running before audio actually starts,
    // which caused phantom progress bars that advance without real playback.
    // For local audio (no rendererId), set 'playing' immediately — the browser
    // audio element starts synchronously and doesn't need confirmation.
    if (rendererId) {
      store.setPlaybackState('loading');
    } else {
      store.setPlaybackState('playing');
    }

    this._flushQueue();
    this._lastPlayUriTimestamp = Date.now();

    // Reset the UPnP ghost-detection grace timer so hi-res FLAC renderers get
    // 20 s to buffer before the poller considers them "ghost" (stuck in STOPPED).
    import('../transport/upnpStatePoller').then(({ notifyPlayDispatched }) => {
      notifyPlayDispatched();
    }).catch(() => {});

    return this._enqueue('playUri', async () => {
      // Exclusive bit-perfect path: decode in Rust, write PCM directly to USB DAC
      if (!rendererId && await this._shouldUseExclusive()) {
        const storeSnap = useMaestroStore.getState();
        const zone = storeSnap.discovery?.activeZone;
        const dacName = resolveExclusiveDeviceName(zone as any, storeSnap.selectedUsbDac);
        if (dacName) {
          console.log('[TCmd] playUri → EXCLUSIVE BIT-PERFECT path for USB DAC:', dacName);
          diagnosticLog('info', 'transport', 'exclusive_play_start', { dac: dacName, rendererType: 'local-native' });
          const C = await this._getConnector();
          await releaseSharedFallbackBeforeExclusive();
          await C.exclusiveStop().catch(() => {});
          const trackIsDsd = isDsd(track);
          const srRaw = track?.sampleRate || track?.sample_rate || 44100;
          const sampleRate = trackIsDsd
            ? (getDsdSampleRateHz(track) ?? 2822400)
            : (srRaw < 1000 ? srRaw * 1000 : srRaw);
          const bd = trackIsDsd ? 1 : (track?.bitDepth || track?.bit_depth || 16);
          try {
            await exclusivePlayWithDeviceBusyRetry(() =>
              C.exclusivePlay(dacName, uri, sampleRate, bd, trackIsDsd),
            );
            logLocalOutputExclusive(dacName, sampleRate, bd);
          } catch (err: any) {
            const msg = err?.message || String(err);
            console.error('[TCmd] Exclusive play failed:', msg);
            logLocalOutputShared(dacName, `exclusive_open_failed:${msg}`);
            const recoverable = isRecoverableExclusiveFailure(msg);
            const logLevel = options?.suppressExclusiveErrorUi || recoverable ? 'info' : 'error';
            diagnosticLog(logLevel, 'transport', 'exclusive_play_failed', {
              dac: dacName,
              error: msg,
              recoverable,
              suppressed: !!options?.suppressExclusiveErrorUi,
            });
            if (!options?.suppressExclusiveErrorUi && shouldShowFatalExclusiveError(msg)) {
              useMaestroStore.getState().setPlaybackState('paused');
              useMaestroStore.getState().setExclusiveError?.(msg);
            }
            throw err;
          }
          clearExclusiveErrorOnPlaybackConfirmed();
          this._startExclusivePoller();
          this._lastPlayUriSuccessAt = Date.now();
          useMaestroStore.getState().setRendererHasStream(true);
          this._playUriFails = 0;
          console.log('[TCmd] playUri EXCLUSIVE SUCCESS');
          return;
        }
      }

      await this._matchUsbDacSampleRate(track);

      const doPlay = async () => {
        if (rendererId && this._isTauri()) {
          const C = await this._getConnector();
          await C.playUri(
            rendererId, uri,
            track?.title || '', track?.artist || '',
            track?.album || '', formatId,
            Math.round(track?.duration || 0),
            track?.artworkUrl || track?.artUrl,
          );
        } else if (rendererId && this._isIOS()) {
          this._iosDispatchPlayUriPayload(uri, rendererId, formatId, track);
        } else if (this._audioRef) {
          // playUri bypasses WebAudioRenderer trackChanged — tear down any
          // loopback/HLS source before loading the new stream URL.
          this.resetLocalAudioElement();
          this._audioRef.src = uri;
          this._audioRef.load();
          this._commitLocalSourceFn?.(uri);
          await this._audioRef.play();
        }
      };

      const RENDERER_NOT_FOUND_RETRIES = 3;
      const RENDERER_NOT_FOUND_DELAY_MS = 4000;

      for (let attempt = 0; attempt <= RENDERER_NOT_FOUND_RETRIES; attempt++) {
        try {
          await doPlay();

          this._lastPlayUriSuccessAt = Date.now();
          const postStore = useMaestroStore.getState();
          postStore.setRendererHasStream(true);
          clearExclusiveErrorOnPlaybackConfirmed();
          this._playUriFails = 0;

          if (rendererId && this._isIOS()) {
            // iOS: native bridge does not push transport state into the WebView —
            // promote optimistically so useRemotePositionTracker wall-clock ticker runs.
            postStore.setPlaybackState('playing');
          } else if (rendererId && postStore.playbackState === 'loading') {
            // Promote to 'playing' after a short delay if the renderer hasn't
            // confirmed PLAYING via TransportState events yet. This handles
            // renderers that don't push transport state callbacks (e.g., some
            // Bluesound devices, old Rygel firmware). TransportState will
            // override this if it fires first.
            setTimeout(() => {
              const s = useMaestroStore.getState();
              if (s.playbackState === 'loading' && s.rendererHasStream) {
                s.setPlaybackState('playing');
              }
            }, 3000);
          }

          if (rendererId && this._isIOS()) {
            const gen = ++this._iosPlayConfirmGen;
            this._iosBackgroundConfirmPlayback(
              uri, rendererId, formatId, track, gen, options, true,
            );
          }

          if (this._isIOS() && rendererId && !options?.skipGhostWatchdog) {
            this._scheduleGhostPlaybackHeal(uri, rendererId, formatId, track);
          }
          return;
        } catch (err: any) {
          const msg = String(err?.message || err || '');
          const isNotFound = msg.toLowerCase().includes('not found');

          if (isNotFound && attempt < RENDERER_NOT_FOUND_RETRIES) {
            console.warn(
              `[TCmd] playUri: renderer "${rendererId}" not found — waiting ${RENDERER_NOT_FOUND_DELAY_MS}ms for rediscovery (attempt ${attempt + 1}/${RENDERER_NOT_FOUND_RETRIES})`,
            );
            await new Promise(r => setTimeout(r, RENDERER_NOT_FOUND_DELAY_MS));
            continue;
          }

          console.error('[TCmd] playUri FAILED: renderer=', rendererId, 'error=', msg || String(err));
          diagnosticLog('error', 'transport', `playUri failed: ${msg || String(err)}`, { renderer_id: rendererId, error: msg || String(err) });
          this._playUriFails++;
          this._playUriLastFail = Date.now();
          if (!options?.suppressExclusiveErrorUi) {
            useMaestroStore.getState().setRendererHasStream(false);
            useMaestroStore.getState().setPlaybackState('paused');
          }
          throw err;
        }
      }
    });
  }

  async stopAllMultizoneZones() {
    if (!this._isMultizoneActive()) return;
    useMaestroStore.getState().setPlaybackState('stopped');
    await this._multizoneStopAll();
  }

  async stopRenderer(rendererId?: string) {
    const rid = rendererId || useMaestroStore.getState().activeConnectorRenderer?.id;
    if (!rid) return;
    const st = useMaestroStore.getState();
    const az = st.discovery?.activeZone as any;
    diagnosticLog(
      'info',
      'transport',
      `Command dispatched: stop`,
      { command: 'stop', target: st.activeConnectorRenderer?.name || rid, rendererType: az?.transport_type || az?.type },
    );
    await _enforceRaatGap();
    if (this._isTauri()) {
      try {
        const C = await this._getConnector();
        await C.stop(rid);
      } catch {}
    } else if (this._isIOS()) {
      this._iosSendCommand(rid, 'stop');
    }
  }

  /**
   * Pause a specific renderer by ID — bypasses the active renderer lookup.
   * Used during zone switch teardown to stop the OLD renderer before
   * activeConnectorRenderer is updated.
   */
  async pauseRenderer(rendererId: string) {
    console.log('[TransportCommand.pauseRenderer]', rendererId, 'isTauri:', this._isTauri(), 'isIOS:', this._isIOS());
    await _enforceRaatGap();
    if (this._isTauri()) {
      try {
        const C = await this._getConnector();
        await C.pause(rendererId);
      } catch {}
    } else if (this._isIOS()) {
      this._iosSendCommand(rendererId, 'pause');
    }
  }

  // === Private: USB DAC sample rate matching ===

  private async _matchUsbDacSampleRate(track: any): Promise<void> {
    if (!this._isTauri()) return;

    const { selectedUsbDac } = useMaestroStore.getState();
    if (!selectedUsbDac?.id) return;

    const { isExclusiveModeEnabledForDevice } = await import('../utils/usbDacNames');
    if (!isExclusiveModeEnabledForDevice(selectedUsbDac)) return;

    const sampleRate = track?.sampleRate || track?.sample_rate;
    if (!sampleRate || typeof sampleRate !== 'number') return;

    const rateHz = sampleRate < 1000 ? sampleRate * 1000 : sampleRate;
    try {
      const C = await this._getConnector();
      const actual = await C.setDeviceSampleRate(selectedUsbDac.id, rateHz);
      console.log(`[TCmd] USB DAC sample rate: requested ${rateHz} Hz, device set to ${actual} Hz`);
    } catch (err) {
      console.warn('[TCmd] Failed to set USB DAC sample rate:', err);
    }
  }

  // === Private: platform detection ===

  private _isTauri(): boolean {
    return detectPlatform().isTauriDesktop;
  }

  /** True ONLY when the Maestro iOS native app bridge is present (NOT Mobile Safari). */
  private _isIOS(): boolean {
    const result = detectPlatform().isIOSNativeApp;
    if (!this._iosCheckLogged) {
      const p = detectPlatform();
      console.log('[TransportCommand] _isIOS:', result, '| platform:', p.deviceName, '| isIOSMobileSafari:', p.isIOSMobileSafari);
      this._iosCheckLogged = true;
    }
    return result;
  }

  // === Private: iOS native bridge (WKWebView) ===

  private _iosPostMessage(action: string, data: Record<string, any>) {
    try {
      const handler = (window as any).webkit?.messageHandlers?.maestroNative;
      if (!handler) {
        console.error('[TransportCommand] iOS bridge not found — cannot send:', action);
        return;
      }
      handler.postMessage({ action, data });
    } catch (err) {
      console.error('[TransportCommand] iOS bridge postMessage failed:', action, err);
    }
  }

  private _iosSendCommand(rendererId: string, command: string, userInitiated = false) {
    this._iosPostMessage('sendCommand', { deviceId: rendererId, command, userInitiated });
  }

  // === Private: mutex queue ===

  private _flushQueue() {
    for (const cmd of this._queue) {
      cmd.reject(new Error('Flushed by playUri'));
    }
    this._queue = [];
  }

  private _enqueue(type: PendingCommand['type'], execute: () => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      this._queue.push({ type, execute, resolve, reject });
      if (!this._busy) this._drain();
    });
  }

  private async _drain() {
    if (this._busy || this._queue.length === 0) return;
    this._busy = true;
    const cmd = this._queue.shift()!;
    const timeout = cmd.type === 'playUri' ? 25000 : this._commandTimeout;
    {
      const s = useMaestroStore.getState();
      const r = s.activeConnectorRenderer;
      const az = s.discovery?.activeZone as any;
      diagnosticLog(
        'info',
        'transport',
        `Command dispatched: ${cmd.type} to ${r?.name || az?.name || 'local'}`,
        { command: cmd.type, target: r?.name || az?.name || 'local', rendererType: az?.transport_type || az?.type },
      );
    }
    try {
      await _enforceRaatGap();
      const timer = new Promise<never>((_, rej) =>
        setTimeout(() => rej(new Error('Command timeout')), timeout)
      );
      await Promise.race([cmd.execute(), timer]);
      cmd.resolve();
    } catch (err: any) {
      const errMsg = String(err?.message || err || '');
      const isTimeout = errMsg.toLowerCase().includes('command timeout')
        || errMsg.toLowerCase().includes('ipc invoke timeout');

      if (cmd.type === 'playUri' && isTimeout && this._isTauri()) {
        const rendererId = useMaestroStore.getState().activeConnectorRenderer?.id;
        try {
          const C = await this._getConnector();
          const stillPlaying = await verifyRendererPlayingAfterTimeout(
            (id) => C.getTransportInfo(id),
            rendererId,
          );
          if (stillPlaying) {
            console.warn(
              '[TCmd:queue] playUri IPC timeout but renderer is PLAYING — treating as success (no retry)',
            );
            diagnosticLog('warn', 'transport', 'playUri timeout verified playing — no retry', {
              rendererId,
            });
            cmd.resolve();
            this._busy = false;
            this._drain();
            return;
          }
        } catch (verifyErr) {
          console.warn('[TCmd:queue] playUri timeout verify failed:', verifyErr);
        }
      }

      console.error(`[TCmd:queue] ${cmd.type} failed:`, err?.message || err);
      {
        const s = useMaestroStore.getState();
        const r = s.activeConnectorRenderer;
        diagnosticLog(
          'error',
          'transport',
          `Transport error: ${err?.message || err}`,
          {
            renderer: r?.name,
            command: cmd.type,
            error: String(err?.message || err),
          },
        );
      }
      if (cmd.type === 'playUri') {
        cmd.reject(err);
      } else {
        cmd.resolve();
      }
    } finally {
      this._busy = false;
      this._drain();
    }
  }
}

export const TransportCommand = new TransportCommandImpl();

/** Full transport stop — tears down RAAT session (iOS/Tauri native stop); clears stream + stopped state. */
export async function stop(rendererId?: string): Promise<void> {
  const { markUserTransportStop } = await import('./remoteAdvanceControl');
  markUserTransportStop();
  const store = useMaestroStore.getState();
  const id = rendererId ?? store.activeConnectorRenderer?.id;
  if (!id) return;
  const stoppedTrack = store.currentTrack?.title || store.currentTrack?.id || '(unknown)';
  console.log('[Transport] stop requested, clearing active URI — track was:', stoppedTrack, 'renderer:', id);
  await TransportCommand.stopRenderer(id);
  store.setRendererHasStream(false);
  store.clearStreamCache();
  store.setPlaybackState('stopped');
}

(window as any).TransportCommand = TransportCommand;
